import type { Suggestion } from "@divespots/contracts";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router";
import { ConfirmSheet } from "../admin/ui.tsx";
import { api } from "../api/client.ts";
import { useSpots } from "../api/index.ts";
import { Button } from "../components/Button.tsx";
import { formatTimestamp } from "../i18n/format.ts";
import { useLocale } from "../i18n/index.ts";
import { StatusChip, suggestionTitle } from "../suggest/ui.tsx";

const PAGE = 20;

/** Where an approved suggestion ended up: the spot (objects live on their spot's page). */
function ResultLink({ s }: { s: Suggestion }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const spots = useSpots();
  // an object edit only knows the object: its spot comes from the suggestion detail
  const detail = useQuery({
    queryKey: ["suggestion", s.id],
    queryFn: ({ signal }) => api.getSuggestion(s.id, signal),
    enabled: s.status === "approved" && s.kind === "object_edit",
    retry: false,
  });
  if (s.status !== "approved") return null;
  const spotId =
    s.kind === "spot_create"
      ? s.resultId
      : s.kind === "spot_edit"
        ? s.targetId
        : s.kind === "object_create"
          ? s.payload.spotId
          : detail.data?.current?.type === "object"
            ? detail.data.current.object.spotId
            : null;
  const slug = spots.data?.find((x) => x.id === spotId)?.slug;
  if (!slug) return null;
  return (
    <Link className="ds-btn ds-btn--tinted" to={`/${locale}/spot/${slug}`}>
      {s.kind.startsWith("object") ? t("suggest.openObject") : t("suggest.openSpot")}
    </Link>
  );
}

/** `/:lang/konto/vorschlaege`: the diver's own suggestions, newest first, with status and reason. */
export function MySuggestionsPage() {
  const { t } = useTranslation();
  const locale = useLocale();
  const qc = useQueryClient();
  const location = useLocation();
  const [withdrawing, setWithdrawing] = useState<Suggestion | null>(null);
  const thanks = (location.state as { thanks?: boolean } | null)?.thanks === true;

  const q = useInfiniteQuery({
    queryKey: ["suggestions", "mine"],
    queryFn: ({ pageParam, signal }) =>
      api.listMySuggestions({ limit: PAGE, ...(pageParam ? { before: pageParam } : {}) }, signal),
    initialPageParam: "",
    getNextPageParam: (last) =>
      last.suggestions.length < PAGE ? undefined : last.suggestions.at(-1)?.createdAt,
  });
  const rows = (q.data?.pages ?? []).flatMap((p) => p.suggestions);

  const withdraw = useMutation({
    mutationFn: (id: string) => api.withdrawSuggestion(id),
    onSuccess: async () => {
      setWithdrawing(null);
      await qc.invalidateQueries({ queryKey: ["suggestions"] });
    },
  });

  return (
    <div className="grid gap-6">
      <h1 className="ds-text-title-1 m-0">{t("diver.suggestions.title")}</h1>
      {thanks ? (
        <p className="adm-ok m-0" role="status">
          {t("suggest.thanks")}
        </p>
      ) : null}
      {q.isPending ? <p role="status">{t("common.loading")}</p> : null}
      {q.isError ? (
        <p className="adm-error" role="alert">
          {t("admin.err.load")}
        </p>
      ) : null}
      {q.isSuccess && rows.length === 0 ? (
        <p className="ds-text-subheadline text-label-secondary m-0">
          {t("diver.suggestions.empty")}
        </p>
      ) : null}

      {rows.length > 0 ? (
        <ul className="ds-list adm-list" aria-label={t("diver.suggestions.title")}>
          {rows.map((s) => (
            <li key={s.id} className="adm-suggestion" data-status={s.status}>
              <div className="adm-suggestion__head">
                <span className="ds-text-headline">{suggestionTitle(s, locale, t)}</span>
                <StatusChip status={s.status} />
              </div>
              <span className="ds-text-subheadline text-label-secondary">
                {t(`suggest.kind.${s.kind}`)} · {formatTimestamp(s.createdAt, locale)}
              </span>
              {s.note ? <p className="ds-text-body m-0">{s.note}</p> : null}
              {s.status === "rejected" && s.reviewReason ? (
                <p className="ds-text-subheadline m-0" data-testid="reject-reason">
                  <strong>{t("suggest.reason")}:</strong> {s.reviewReason}
                </p>
              ) : null}
              {s.status === "approved" && s.reviewReason ? (
                <p className="ds-text-subheadline m-0">{s.reviewReason}</p>
              ) : null}
              <div className="adm-actions">
                <ResultLink s={s} />
                {s.status === "pending" ? (
                  <Button
                    variant="tinted"
                    aria-label={t("suggest.withdrawNamed", { name: suggestionTitle(s, locale, t) })}
                    onClick={() => setWithdrawing(s)}
                  >
                    {t("suggest.withdraw")}
                  </Button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      {q.hasNextPage ? (
        <div className="adm-actions">
          <Button
            variant="tinted"
            disabled={q.isFetchingNextPage}
            onClick={() => void q.fetchNextPage()}
          >
            {t("suggest.more")}
          </Button>
        </div>
      ) : null}

      {withdrawing ? (
        <ConfirmSheet
          title={t("suggest.withdrawTitle")}
          body={t("suggest.withdrawBody")}
          confirmLabel={t("suggest.withdraw")}
          busy={withdraw.isPending}
          error={withdraw.isError ? t("admin.err.save") : undefined}
          onConfirm={() => withdraw.mutate(withdrawing.id)}
          onCancel={() => setWithdrawing(null)}
        />
      ) : null}
    </div>
  );
}
